/* global */
import React, { useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Link, LinkProps, useNavigate, useLocation } from 'react-router';
import cx from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { SignOutButton } from '@birdi/main-navigation/SignOut';
import { MobileMenu as MobileMenuBase } from '@birdi/main-navigation/MobileMenu';
import { BirdiTheme, blocks } from '@birdi/theme';
import BirdiIcon, { Icons } from '@birdi/icons';
import ExternalLinkSVG from '@birdi/icons/external-link.svg';
import UpgradeSVG from '@birdi/icons/upgrade.svg';
import { FeatureFlagNames } from '@birdi/js-sdk/src/public/organisation-features';
import { useMetaSelector } from '../../meta/reducer';
import { hasSites } from '../../map-sites/site-features';

type MenuItemLinkProps = LinkProps & {
  match: string;
  missions?: boolean;
  hide: () => void;
};

const MenuItemLink: React.FC<MenuItemLinkProps> = ({
  to,
  match,
  children,
  missions = false,
  className = '',
  hide,
  id = null,
}) => {
  const otherProps: Partial<LinkProps> = {};
  if (id) otherProps.id = id;
  return (
    <Link
      className={cx(className, { selected: to === match && !missions })}
      to={to}
      onClick={() => {
        hide();
      }}
      {...otherProps}
    >
      {children}
    </Link>
  );
};

export const MobileMenu = () => {
  const meta = useMetaSelector();
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedMenuItem, setExpandedMenuItem] = useState({
    missions: false,
    pilotNetwork: false,
    sites: false,
    help: false,
  });
  const expandedMissionsRef = useRef<HTMLDivElement>(null);
  const expandedPilotNetworkRef = useRef<HTMLDivElement>(null);
  const expandedSitesRef = useRef<HTMLDivElement>(null);
  const expandedHelpRef = useRef<HTMLDivElement>(null);

  const costCalculator = meta.orgFeatureFlags.includes(
    FeatureFlagNames.GigapixelCalculator,
  );
  const sites = hasSites(meta.features);

  const isOrganisationAdmin = meta.permissions.includes(
    'organisation:user:create',
  );
  const canEditWorkspace = meta.permissions.includes('workspace:edit');
  const canCreateMaps = meta.permissions.includes('workspace:map:create');

  const upgradeDisabled = !isOrganisationAdmin;
  const mapsV2 = meta.orgFeatureFlags.includes(FeatureFlagNames.MapsV2);
  const hasMissionsAccess = meta.orgFeatureFlags.includes(
    FeatureFlagNames.Missions,
  );

  return (
    <MobileMenuBase>
      {({ hide }) => (
        <div
          css={(theme: BirdiTheme) => css`
            .item {
              display: flex;
              width: 100%;
              color: ${theme.displayFont};
              padding: 0.5em;
              font-size: 0.9375em;
              align-items: center;
              gap: 0.5rem;
              &.selected {
                color: ${theme.deepOcean};
              }
              &.main {
                font-size: 1em;
                font-weight: 700;
              }
              &.link-disabled {
                pointer-events: none;
                color: ${theme.mono40};
              }
              .icon-chevron {
                margin-left: auto;
              }
            }
          `}
        >
          <MenuItemLink
            to="/"
            match={location.pathname}
            hide={hide}
            className={cx('item', 'main')}
          >
            <span>Dashboard</span>
          </MenuItemLink>
          <hr className="divider" />
          <button
            className={cx('item', 'main')}
            type="button"
            onClick={() =>
              setExpandedMenuItem((prev) => ({
                ...prev,
                missions: !prev.missions,
              }))
            }
            css={css`
              padding: 0;
            `}
          >
            <span>Maps</span>
            <BirdiIcon
              icon={Icons.Chevron}
              rotate={expandedMenuItem.missions ? '180' : '90'}
            />
          </button>
          <div
            css={css`
              ${blocks.overlayMenuContainerCSS}
            `}
          >
            <CSSTransition
              in={expandedMenuItem.missions}
              timeout={150}
              classNames="drop-menu"
              nodeRef={expandedMissionsRef}
              unmountOnExit
            >
              <div ref={expandedMissionsRef}>
                {mapsV2 && (
                  <MenuItemLink
                    hide={hide}
                    to="/new-maps/all"
                    match={location.pathname}
                    className={cx('item', 'sub')}
                  >
                    <span
                      css={css`
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                      `}
                    >
                      Birdi 2.0 Maps
                    </span>
                  </MenuItemLink>
                )}
                {/* {canCreateMaps && (
                  <MenuItemLink
                    hide={hide}
                    to="/missions/mine"
                    match={history.location.pathname}
                    className={cx('item', 'sub')}
                  >
                    <span>My Maps</span>
                  </MenuItemLink>
                )} */}
                <MenuItemLink
                  hide={hide}
                  to="/missions/shared"
                  match={location.pathname}
                  className={cx('item', 'sub')}
                >
                  <span>Shared With Me</span>
                </MenuItemLink>
                <MenuItemLink
                  hide={hide}
                  to="/missions/organisation"
                  match={location.pathname}
                  className={cx('item', 'sub')}
                >
                  <span>All Maps</span>
                </MenuItemLink>
              </div>
            </CSSTransition>
          </div>
          <hr className="divider" />
          {meta.jobNotifications && (
            <>
              <button
                className={cx('item', 'main')}
                type="button"
                onClick={() =>
                  setExpandedMenuItem((prev) => ({
                    ...prev,
                    pilotNetwork: !prev.pilotNetwork,
                  }))
                }
                css={css`
                  padding: 0;
                `}
              >
                <span>Pilot Network</span>
                <BirdiIcon
                  icon={Icons.Chevron}
                  rotate={expandedMenuItem.pilotNetwork ? '180' : '90'}
                />
              </button>
              <div
                css={css`
                  ${blocks.overlayMenuContainerCSS}
                `}
              >
                <CSSTransition
                  in={expandedMenuItem.pilotNetwork}
                  timeout={150}
                  classNames="drop-menu"
                  nodeRef={expandedPilotNetworkRef}
                  unmountOnExit
                >
                  <div ref={expandedPilotNetworkRef}>
                    <MenuItemLink
                      hide={hide}
                      to="/job-requests/incoming"
                      match={location.pathname}
                      className={cx('item', 'sub')}
                    >
                      <span>Contract Requests</span>
                    </MenuItemLink>
                    <MenuItemLink
                      hide={hide}
                      to="/missions/mine"
                      match={location.pathname}
                      className={cx('item', 'sub')}
                    >
                      <span>Capture Requests</span>
                    </MenuItemLink>
                  </div>
                </CSSTransition>
              </div>
              <hr className="divider" />
            </>
          )}
          {sites && (
            <>
              <button
                className={cx('item', 'main')}
                type="button"
                onClick={() =>
                  setExpandedMenuItem((prev) => ({
                    ...prev,
                    sites: !prev.sites,
                  }))
                }
                css={css`
                  padding: 0;
                `}
              >
                <span>Sites</span>
                <BirdiIcon
                  icon={Icons.Chevron}
                  rotate={expandedMenuItem.sites ? '180' : '90'}
                />
              </button>
              <div
                css={css`
                  ${blocks.overlayMenuContainerCSS}
                `}
              >
                <CSSTransition
                  in={expandedMenuItem.sites}
                  timeout={150}
                  classNames="drop-menu"
                  nodeRef={expandedSitesRef}
                  unmountOnExit
                >
                  <div ref={expandedSitesRef}>
                    <MenuItemLink
                      hide={hide}
                      to="/sites"
                      match={location.pathname}
                      className={cx('item', 'sub')}
                    >
                      <span>All Sites</span>
                    </MenuItemLink>
                  </div>
                </CSSTransition>
              </div>
              <hr className="divider" />
            </>
          )}
          <MenuItemLink
            to={canEditWorkspace ? '/admin-general' : '#'}
            match={location.pathname}
            hide={hide}
            className={cx('item', 'main', {
              'link-disabled': !canEditWorkspace,
            })}
          >
            <span>Administration</span>
          </MenuItemLink>
          <MenuItemLink
            to="/admin-users"
            match={location.pathname}
            hide={hide}
            className={cx('item', 'main')}
          >
            <span>User Management</span>
          </MenuItemLink>
          <MenuItemLink
            to={`/profile-general/${meta.scopeId}`}
            match={location.pathname}
            hide={hide}
            className={cx('item', 'main')}
          >
            <span>My Profile</span>
          </MenuItemLink>
          {hasMissionsAccess && (
            <MenuItemLink
              to="/assessment-types"
              match={location.pathname}
              hide={hide}
              className={cx('item', 'main')}
            >
              <span>Inspection Tools</span>
            </MenuItemLink>
          )}
          <MenuItemLink
            to="/user/pilot"
            match={location.pathname}
            hide={hide}
            className={cx('item', 'main')}
          >
            <span>Pilot Settings</span>
          </MenuItemLink>
          <hr className="divider" />
          <button
            className={cx('item', 'main')}
            type="button"
            onClick={() =>
              setExpandedMenuItem((prev) => ({ ...prev, help: !prev.help }))
            }
            css={css`
              padding: 0;
            `}
          >
            <span>Help</span>
            <BirdiIcon
              icon={Icons.Chevron}
              rotate={expandedMenuItem.help ? '180' : '90'}
            />
          </button>
          <CSSTransition
            in={expandedMenuItem.help}
            timeout={150}
            classNames="drop-menu"
            nodeRef={expandedHelpRef}
            unmountOnExit
          >
            <div ref={expandedHelpRef}>
              {/* {costCalculator && (
                <MenuItemLink
                  hide={hide}
                  to="/cost-calculator-archived"
                  match={history.location.pathname}
                  className={cx('item', 'sub')}
                >
                  <span>Processing Cost Calculator</span>
                </MenuItemLink>
              )} */}
              <Link
                to={{ pathname: 'https://help.birdi.io/' }}
                target="_blank"
                className={cx('item', 'sub')}
              >
                <span>Knowledge Base</span>
                <ExternalLinkSVG />
              </Link>
              <Link
                to={{ pathname: 'https://www.birdi.io/partner-program' }}
                target="_blank"
                className={cx('item', 'sub')}
              >
                <span>Partner Program</span>
                <ExternalLinkSVG />
              </Link>
              <Link
                to={{ pathname: 'https://www.birdi.io/terms-and-conditions' }}
                target="_blank"
                className={cx('item', 'sub')}
              >
                <span>Terms & Conditions</span>
                <ExternalLinkSVG />
              </Link>
            </div>
          </CSSTransition>
          <hr className="divider" />
          <SignOutButton />
          {!upgradeDisabled && (
            <button type="button" onClick={() => navigate('/billing/options')}>
              <UpgradeSVG
                css={css`
                  font-size: 2.5em;
                  vertical-align: middle;
                  margin-right: 0;
                `}
              />
              <span
                css={(theme: BirdiTheme) => css`
                  vertical-align: middle;
                  color: ${theme.deepOcean};
                  font-weight: bold;
                `}
              >
                Upgrade
              </span>
            </button>
          )}
        </div>
      )}
    </MobileMenuBase>
  );
};
